import React, { useEffect, useState } from "react";
import { hotelApi } from "./hotelApi";

export default function HotelStaysScreen({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [status, setStatus] = useState("all");

  const [form, setForm] = useState({
    guest_id: "",
    guest_name: "",
    guest_phone: "",
    guest_email: "",
    room_id: "",
    check_in: "",
    check_out: "",
    status: "checked_in",
    notes: "",
    rate: ""
  });

  const showMsg = (m) => {
    setMsg(m);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 4000);
  };

  /* ================= LOAD DATA ================= */

  const loadStays = async () => {
    try {
      setLoading(true);
      const res = await hotelApi.get("/stays");
      setRows(res.data?.rows || []);
    } catch (err) {
      console.error(err);
      showMsg("❌ Failed to load stays");
    } finally {
      setLoading(false);
    }
  };

  const loadRooms = async () => {
    try {
      const res = await hotelApi.get("/rooms");
      setRooms(res.data?.rows || []);
    } catch (err) {
      console.error(err);
      showMsg("❌ Failed to load rooms");
    }
  };

  const loadGuests = async () => {
    try {
      const res = await hotelApi.get("/guests");
      setGuests(res.data?.rows || []);
    } catch (err) {
      console.error(err);
      showMsg("❌ Failed to load guests");
    }
  };

  useEffect(() => {
    loadStays();
    loadRooms();
    loadGuests();
  }, []);

  /* ================= SAVE STAY ================= */

  const handleSave = async () => {
    try {
      if (!form.room_id) {
        showMsg("❌ Please select a room");
        return;
      }

      if (!form.check_in) {
        showMsg("❌ Check-in date required");
        return;
      }

      if (!form.guest_id && !form.guest_name.trim()) {
        showMsg("❌ Select or enter guest name");
        return;
      }

      const payload = {
        guest_id: form.guest_id ? Number(form.guest_id) : 0,
        guest_name: form.guest_name.trim(),
        guest_phone: form.guest_phone.trim(),
        guest_email: form.guest_email.trim(),
        room_id: Number(form.room_id), // 🔥 FIX
        check_in: form.check_in,
        check_out: form.check_out,
        status: form.status,
        notes: form.notes,
        rate: form.rate
      };

      console.log("Submitting stay:", payload);

      const res = await hotelApi.post("/stays", payload);

      showMsg("✅ Stay created");

      await loadStays();
      await loadGuests();

      setForm({
        guest_id: "",
        guest_name: "",
        guest_phone: "",
        guest_email: "",
        room_id: "",
        check_in: "",
        check_out: "",
        status: "checked_in",
        notes: "",
        rate: ""
      });
    } catch (err) {
      console.error(err);
      showMsg(err?.response?.data?.error || "❌ Failed to save stay");
    }
  };

  /* ================= UI ================= */

  return (
    <div style={styles.wrap}>
      <h2>🏨 Hotel Stays</h2>

      {/* -------- FORM -------- */}
      <div style={styles.card}>
        <h3>Create Stay</h3>

        <div style={styles.grid}>

          {/* EXISTING GUEST */}
          <select
            value={form.guest_id}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                guest_id: e.target.value,
                guest_name: ""
              }))
            }
          >
            <option value="">Select Guest</option>
            {guests.map((g) => (
              <option key={g.id} value={g.id}>
                {g.full_name}
              </option>
            ))}
          </select>

          {/* NEW GUEST */}
          <input
            placeholder="New Guest Name"
            value={form.guest_name}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                guest_name: e.target.value,
                guest_id: ""
              }))
            }
          />

          <input
            placeholder="Phone"
            value={form.guest_phone}
            onChange={(e) =>
              setForm((f) => ({ ...f, guest_phone: e.target.value }))
            }
          />

          <input
            placeholder="Email"
            value={form.guest_email}
            onChange={(e) =>
              setForm((f) => ({ ...f, guest_email: e.target.value }))
            }
          />

          {/* ROOM */}
          <select
            value={form.room_id}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                room_id: e.target.value
              }))
            }
          >
            <option value="">Select Room</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.room_no} ({r.room_type})
              </option>
            ))}
          </select>

          <input
            type="date"
            value={form.check_in}
            onChange={(e) =>
              setForm((f) => ({ ...f, check_in: e.target.value }))
            }
          />

          <input
            type="date"
            value={form.check_out}
            onChange={(e) =>
              setForm((f) => ({ ...f, check_out: e.target.value }))
            }
          />

          <input
            placeholder="Rate"
            value={form.rate}
            onChange={(e) =>
              setForm((f) => ({ ...f, rate: e.target.value }))
            }
          />
        </div>

        <button style={styles.btnPrimary} onClick={handleSave}>
          ➕ Save Stay
        </button>
      </div>

      {msg && <div style={styles.msg}>{msg}</div>}

      {/* -------- TABLE -------- */}
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th>Room</th>
              <th>Guest</th>
              <th>Check-in</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id}>
                <td>{s.room_no}</td>
                <td>{s.guest_name}</td>
                <td>{s.check_in}</td>
                <td>{s.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const styles = {
  wrap: { padding: 20 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10,
    marginBottom: 10
  },
  card: {
    background: "#fff",
    padding: 15,
    marginBottom: 15,
    borderRadius: 10
  },
  btnPrimary: {
    padding: 10,
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  table: { width: "100%" },
  msg: { marginTop: 10 }
};